// Luxe · Approval Stage Panel — presents the client acceptance /
// feedback state clearly, using existing proposals / proposal_versions
// / proposal_acceptances / activities. Does NOT build a new approval
// backend. Client View safe: never exposes internal cost / rate data.
import { formatAUD } from "@/lib/api";
import { CheckCircle2, AlertCircle, Clock } from "lucide-react";

function feedbackFromActivities(activities) {
  const feedback = (activities || []).filter(
    (a) => a.type === "portal_feedback_submitted" || a.type === "client_changes_requested",
  );
  if (!feedback.length) return null;
  return [...feedback].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
}

function acceptanceFromProposals(proposals) {
  const versions = (proposals || []).flatMap((p) => p.versions || []);
  const accepted = versions.filter((v) => v.status === "accepted");
  if (!accepted.length) return null;
  return [...accepted].sort((a, b) => new Date(b.accepted_at || 0) - new Date(a.accepted_at || 0))[0];
}

function latestSent(proposals) {
  const versions = (proposals || []).flatMap((p) => p.versions || []);
  const sent = versions.filter((v) => v.status === "sent" || v.status === "viewed");
  if (!sent.length) return null;
  return [...sent].sort((a, b) => new Date(b.issued_at || 0) - new Date(a.issued_at || 0))[0];
}

export default function ApprovalStagePanel({
  project,
  proposals,
  activities,
  sceneVersions,
  onReviewDesign,
  onOpenProposal,
  clientView = false,
}) {
  const acceptance = acceptanceFromProposals(proposals);
  const feedback = feedbackFromActivities(activities);
  const sent = latestSent(proposals);
  const hasAnyProposal = (proposals || []).some((p) => (p.versions || []).length > 0);

  const approvedOption = (sceneVersions || []).find((o) => o.status === "approved") || null;

  // Priority: accepted > changes requested > awaiting > not ready.
  if (acceptance) {
    return (
      <div className="luxe-card p-8" data-testid="approval-accepted">
        <div className="flex items-start gap-4">
          <CheckCircle2 className="w-6 h-6 mt-1 shrink-0 text-emerald-700" strokeWidth={1.5}/>
          <div className="min-w-0 flex-1">
            <div className="overline text-[10px]" style={{ color: "#A68A64" }}>Project approved</div>
            <h3 className="font-serif font-light mt-2" style={{ fontSize: "26px", lineHeight: 1.1 }}>
              The client has accepted this proposal.
            </h3>
            <div className="grid sm:grid-cols-3 gap-6 mt-6 text-sm">
              <div data-testid="approval-accepted-date">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Accepted</div>
                <div className="mt-1">
                  {acceptance.accepted_at ? new Date(acceptance.accepted_at).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" }) : "—"}
                </div>
              </div>
              <div data-testid="approval-accepted-value">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Investment</div>
                <div className="mt-1 font-medium">
                  {typeof acceptance.totals_cents?.total === "number"
                    ? formatAUD(acceptance.totals_cents.total / 100)
                    : "—"}
                </div>
              </div>
              <div data-testid="approval-approved-design">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Approved design</div>
                <div className="mt-1 truncate">{approvedOption?.name || "—"}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (feedback) {
    return (
      <div className="luxe-card p-8 border-l-2 border-[color:hsl(var(--accent))]"
           data-testid="approval-changes-requested">
        <div className="flex items-start gap-4">
          <AlertCircle className="w-6 h-6 mt-1 shrink-0 text-[color:hsl(var(--accent))]" strokeWidth={1.5}/>
          <div className="min-w-0 flex-1">
            <div className="overline text-[10px]" style={{ color: "#A68A64" }}>Changes requested</div>
            <h3 className="font-serif font-light mt-2" style={{ fontSize: "22px", lineHeight: 1.15 }}>
              The client has requested changes.
            </h3>
            <div className="mt-4 bg-muted/40 border border-border p-4 text-sm"
                 data-testid="approval-feedback-message">
              {feedback.description || "Client submitted feedback on the current proposal."}
            </div>
            <div className="text-xs text-muted-foreground mt-2">
              {new Date(feedback.created_at).toLocaleString("en-AU")}
            </div>
            {!clientView && (
              <button type="button"
                      data-testid="btn-approval-review-design"
                      onClick={onReviewDesign}
                      className="btn-primary text-sm mt-6">
                Review design <span aria-hidden>&rarr;</span>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (sent) {
    return (
      <div className="luxe-card p-8" data-testid="approval-awaiting">
        <div className="flex items-start gap-4">
          <Clock className="w-6 h-6 mt-1 shrink-0 text-muted-foreground" strokeWidth={1.5}/>
          <div className="min-w-0 flex-1">
            <div className="overline text-[10px]">Awaiting client</div>
            <h3 className="font-serif font-light mt-2" style={{ fontSize: "22px", lineHeight: 1.15 }}>
              Waiting for the client to review the proposal.
            </h3>
            <p className="text-sm text-muted-foreground mt-3">
              You'll see feedback or acceptance appear here as soon as the client responds through their portal.
            </p>
            {sent.issued_at && (
              <div className="text-xs text-muted-foreground mt-3" data-testid="approval-sent-at">
                Sent {new Date(sent.issued_at).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Not ready — no proposal sent yet.
  return (
    <div className="luxe-card p-8 text-center" data-testid="approval-not-ready">
      <div className="overline text-[10px] text-muted-foreground">Not ready for approval yet</div>
      <div className="font-serif text-xl mt-3">
        {hasAnyProposal ? "Send the proposal to your client to begin approval." : "Create and send a proposal to begin approval."}
      </div>
      <p className="text-xs text-muted-foreground mt-3 max-w-md mx-auto">
        Approval status, client feedback and acceptance will appear here automatically once the client responds.
      </p>
    </div>
  );
}
